import { httpClient } from "@/shared/services/http";

import { IAssessmentData } from "@/modules/assessments/scenes/CreateAssessment/assessment.interface";
import { IAssessment, ICandidateAssessment, ICandidateAssessmentsData } from "@/modules/assessments/assessments.types";
import { LanguagesIsoEnum } from "@/modules/assessments/types/test.interface";
import {
  FeedbackConfigResponse,
  AvailableFeedbackQuestionsResponse,
} from "@/modules/candidate/feedback/candidate-feedback.types";
import { AxiosResponse } from "axios";

export type AssessmentStatusFilter = "all" | "active" | "archived";

export interface GetAssessmentsParameters {
  take?: number;
  skip?: number;
  sortOptions?: { column: string | undefined; direction: string | undefined };
  search?: string;
  testNamesFilter?: string[];
  status?: AssessmentStatusFilter;
}

export class AssessmentHttpService {
  static async getAllAssessments(clientId: number, options: GetAssessmentsParameters = {}) {
    const { skip, take, sortOptions, testNamesFilter, status } = options;

    const searchParams = new URLSearchParams();
    if (options?.search) {
      searchParams.set("search", options.search);
    }
    if (sortOptions?.column && sortOptions?.direction) {
      searchParams.set("sortBy", sortOptions.column);
      searchParams.set("sort", sortOptions.direction);
    }
    if (Array.isArray(testNamesFilter) && testNamesFilter.length > 0) {
      searchParams.set("testNames", testNamesFilter.join(","));
    }
    if (typeof skip === "number" && typeof take === "number") {
      searchParams.set("take", String(take));
      searchParams.set("skip", String(skip));
    }
    if (status && status !== "all") {
      searchParams.set("status", status);
    }

    const search = searchParams.toString();
    const url = `/api/clients/${clientId}/assessments` + (search ? `?${search}` : "");
    const { data: assessments } = await httpClient.get(url);

    return assessments;
  }

  static async getAssessmentByID(assessmentId: number): Promise<IAssessment> {
    const { data: assessment } = await httpClient.get(`/api/assessments/${assessmentId}`);
    return assessment;
  }

  // Without a locale the endpoint returns the raw locale-keyed map, so the locale is required here.
  static async getCandidateAssessmentByID({
    assessmentId,
    locale,
  }: {
    assessmentId: number;
    locale: LanguagesIsoEnum;
  }): Promise<ICandidateAssessment> {
    const { data: assessment } = await httpClient.get(`/api/assessments/${assessmentId}`, { params: { locale } });
    return assessment;
  }

  static async getAssessmentPreview(params: { code?: string; assessmentId?: string }) {
    return await httpClient.get(`/api/assessments/preview`, {
      params,
    });
  }

  static async removeAssessmentByID(clientId: number, assessmentId: number) {
    const { data: assessment } = await httpClient.delete(`/api/clients/${clientId}/assessments/${assessmentId}`);
    return assessment;
  }

  static async archiveAssessmentByID(clientId: number, assessmentId: number, options: { keepTeam: boolean }) {
    const { data: assessment } = await httpClient.post(
      `/api/clients/${clientId}/assessments/${assessmentId}/archive`,
      options,
    );
    return assessment;
  }

  static async createNewAssessment(clientId: number, postData: IAssessmentData) {
    return httpClient.post(`/api/clients/${clientId}/assessments`, postData);
  }

  static async updateAssessment(clientId: number, assessmentId: number, postData: IAssessmentData) {
    return httpClient.patch(`/api/clients/${clientId}/assessments/${assessmentId}`, postData);
  }

  static async getAllTextLocations() {
    const { data: textLocations } = await httpClient.get("/api/text-locations");
    return textLocations;
  }

  static async createInvite(assessmentId: number) {
    return httpClient.post(`/api/assessments/${assessmentId}/invites`);
  }

  static async inviteToAssessment(assessmentId: number, candidates: Blob) {
    const candidatesForm = new FormData();
    candidatesForm.append("file", candidates);

    const { data } = await httpClient.post(`/api/assessments/${assessmentId}/candidates/invites`, candidatesForm);

    return data;
  }

  static async cloneAssessment(clientId: number, assessmentId: number) {
    return httpClient.post(`/api/clients/${clientId}/assessments/${assessmentId}/clone`);
  }

  static async fetchCandidateAssessmentsData(userId: number): Promise<ICandidateAssessmentsData> {
    const { data } = await httpClient.get(`api/assessments/user/${userId}`);

    return data;
  }

  static async exportAssessment(clientId: number, assessmentId: number): Promise<AxiosResponse<Blob>> {
    return httpClient.get(`api/clients/${clientId}/assessments/${assessmentId}/export-report/download`, {
      responseType: "blob",
    });
  }

  static async getFeedbackConfig(assessmentId: number): Promise<FeedbackConfigResponse> {
    const { data } = await httpClient.get<FeedbackConfigResponse>(`/api/assessments/${assessmentId}/feedback/config`);
    return data;
  }

  static async getAvailableFeedbackQuestions(): Promise<AvailableFeedbackQuestionsResponse> {
    const { data } = await httpClient.get<AvailableFeedbackQuestionsResponse>(
      `/api/assessments/feedback/available-questions`,
    );
    return data;
  }
}
